import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  View as RNView,
  Modal,
  Pressable,
  Platform,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Feather, MaterialCommunityIcons } from "@expo/vector-icons";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/shared/Spinner";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import {
  mdOrderTrackingHeaders,
  nestedOrderTrackingHeaders,
  orderTrackingHeaders,
  smOrderTrackingHeaders,
} from "./utils/headers";
import {
  filterSpecificField,
  searchTrackerOrders,
  setChinaLanguage,
  sortTrackerOrdersByOrderNumber,
  filterTrackerOrders,
} from "../../redux/reducers/chinaTrackerReducer";
import { ItemTrackerBadgeHorizontalList } from "../../components/custom/Badge/ItemTrackerBadgeHorizontalList";
import { ChinaTrackerDataTableWithPagination } from "../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination";
import ChinaTrackerFlagModal from "./partials/ChinaTrackerFlagModal";
import ChinaTrackerIncomingOrders from "./partials/ChinaTrackerIncomingOrders";
import { getMergedTrackerOrders } from "../../redux/dispatcher/chinaTrackerDispatcher";
import { ChinaTrackerProductionLineList } from "../../components/custom/Badge/ChinaTrackerProductionLineList";
import TrackerEnquiryModal from "./partials/TrackerEnquiryModal";
import TrackerHistoryModal from "./partials/TrackerHistoryModal";
import TrackerDateModal from "./partials/TrackerDateModal";
import { translateHeaders, useTranslation } from "../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../utils/translations";
import ChinaTrackerRejectedOrders from "./partials/ChinaTrackerRejectedOrders";
import { ConfigurationDispatcher } from "../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../redux/constants/actionTypesConfigurations";
import TrackerPriorityModal from "./partials/TrackerPriorityModal";
import ChecklistModal from "./partials/ChinaChecklistModal";
import ChinaShipmentsListModal from "./partials/ChinaShipmentsListModal";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import { exportDataToExcelMultiSheet } from "./utils/exportDataToExcelMultiSheet";
import ChinaTrackerItemCodeInfoModal from "./partials/ChinaTrackerItemCodeInfoModal";
import ChinaTrackerRequestDateAlertsModal from "./partials/ChinaTrackerRequestDateAlertsModal ";
import ChinaTrackerInterruptRunOrder from "./partials/ChinaTrackerInterruptRunOrder";
import ChinaTrackerAdvancedSearch from "./partials/ChinaTrackerAdvancedSearch";
import ChinaTrackerCancelledOrders from "./partials/ChinaTrackerCancelledOrders";

export default function ChinaTrackerScreen({ navigation }: any) {
  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProductionLine, setSelectedProductionLine] = useState<
    string | number
  >("All");
  const [expandQuickFilter, setExpandQuickFilter] = useState<boolean>(false);
  const [areFiltersExpanded, setAreFiltersExpanded] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<
    "production" | "status" | null
  >(null);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedProductionLines, setSelectedProductionLines] = useState<
    (string | number)[]
  >([]);

  const [page, setPage] = useState<number>(0);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      orderTrackingHeaders,
    );
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("chinatracker_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  // All available column definitions (translated)
  const getAllTrackerHeaders = () => {
    const baseHeaders = isLargeDevice
      ? orderTrackingHeaders
      : isMediumDevice
        ? mdOrderTrackingHeaders
        : smOrderTrackingHeaders;
    return translateHeaders(baseHeaders, t, chinaTrackerTranslationMap);
  };

  // Build filtered + normalized headers from hidden columns
  const getOrderedKeys = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const allKeys = Object.keys(baseHeaders);
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k) => { if (allKeys.includes(k)) ordered.push(k); });
    allKeys.forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map((key) => [key, baseHeaders[key]] as [string, Record<string, string | number | boolean>]);
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("chinatracker_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const moveColumn = useCallback((fromKey: string, toKey: string) => {
    const allHeaders = getAllTrackerHeaders();
    const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const newKeys = [...currentKeys];
    newKeys.splice(fromIdx, 1);
    newKeys.splice(toIdx, 0, fromKey);
    newKeys.push("");
    setColumnOrder(newKeys);
    AsyncStorage.setItem("china_tracker_column_order", JSON.stringify(newKeys));
  }, [columnOrder]);

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("china_tracker_column_order");
  };

  // Drag-and-drop reordering via window mouse events
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({ activeKey: null, overKey: null });

  const startColumnDrag = useCallback((key: string) => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    dragRef.current.activeKey = key;
    setDragActiveKey(key);

    const onMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el && (el as HTMLElement).closest ? (el as HTMLElement).closest("[data-columnkey]") : null;
      const overKey = row ? row.getAttribute("data-columnkey") : null;
      if (overKey !== dragRef.current.overKey) {
        dragRef.current.overKey = overKey;
        setDragOverKey(overKey && overKey !== dragRef.current.activeKey ? overKey : null);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      (document.body.style as any).userSelect = "";
      const from = dragRef.current.activeKey;
      const to = dragRef.current.overKey;
      if (from && to && from !== to) {
        moveColumn(from, to);
      }
      dragRef.current = { activeKey: null, overKey: null };
      setDragActiveKey(null);
      setDragOverKey(null);
    };

    document.body.style.cursor = "grabbing";
    (document.body.style as any).userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [moveColumn]);

  const screenWidth = Dimensions.get("window").width;
  const shouldStackUtilityCards =
    isSmallDevice || isMobileDevice || screenWidth <= 1400;
  const shouldStackFilters =
    isSmallDevice || isMobileDevice || screenWidth <= 1200;
  const shouldUseCompactControls =
    isSmallDevice || isMobileDevice || screenWidth <= 1366;

  const { t } = useTranslation();
  const columnsToNotHaveThousandsSeparator: Array<string> = ["Packing #"];
  const operationsFilterCategoryList = [
    t("all"),
    t("atRisk"),
    t("delayed"),
    t("interrupted"),
    t("noData"),
    t("onTime"),
    t("planned"),
    t("completed"),
  ];
  const productionLineFilterList = [
    "All",
    0,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    13,
    14,
  ];
  const [expandProductionLine, setExpandProductionLine] =
    useState<boolean>(false);

  const { userInfo, userRoles, language } = useSelector(
    (state: any) => state.configurations,
  );

  const dispatch = useDispatch<any>();

  const [bellStatus, setBellStatus] = useState<boolean>(true);
  const [incomingBellStatus, setIncomingBellStatus] = useState<boolean>(true);

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);
  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };
  const [nestedTableHeaders, setNestedTableHeaders] = useState<
    Record<string, Record<string, string | number | boolean>>
  >(
    translateHeaders(nestedOrderTrackingHeaders, t, chinaTrackerTranslationMap),
  );
  const {
    loading,
    filteredOrders,
    sortDirection,
    approvedOrders: orders,
    incomingOrders,
    unRevisedRejectedOrders,
    isChinaTrackerLoaded,
    isLanguageSet,
  } = useSelector((state: any) => state.chinaTracker);

  const fetchTrackerOrders = () => {
    dispatch(getMergedTrackerOrders({ refresh: isRefresh }));
  };
  const navigateToOrder = (order: any, action: string) => {
    if (action === "view") {
      navigation.navigate("ChinaTrackerDetailsReport", { order });
    } else {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  const initialFilterDone = useRef(false);
  const isRefreshingRef = useRef(false);
  const initialFetchDone = useRef(false);

  useEffect(() => {
    if (isRefresh) {
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;
      const performRefresh = async () => {
        try {
          await dispatch(getMergedTrackerOrders({ refresh: true })).unwrap();
          // The data fetch completely finished and redux updated filteredOrders.
          // Now safely re-apply the user's active Quick Filters and Production Line.
          dispatch(
            filterTrackerOrders({
              items: selectedCategories,
              productionLines: selectedProductionLines,
              productionLine: selectedProductionLine,
              language: language,
            }),
          );
          initialFilterDone.current = true;
        } catch (error) {
          console.error("Refresh failed", error);
        } finally {
          isRefreshingRef.current = false;
          setRefresh(false);
        }
      };

      performRefresh();
      return;
    }

    if (!isRefresh) {
      if (
        (!orders || (orders.length === 0 && !isChinaTrackerLoaded)) &&
        !initialFetchDone.current
      ) {
        initialFetchDone.current = true;
        fetchTrackerOrders();
      } else if (isChinaTrackerLoaded && orders && orders.length > 0) {
        // Re-apply filter if not done yet (initial load only, refresh is handled above)
        if (!initialFilterDone.current && !loading) {
          initialFilterDone.current = true;
          dispatch(
            filterTrackerOrders({
              items: selectedCategories,
              productionLines: selectedProductionLines,
              productionLine: selectedProductionLine,
              language: language,
            }),
          );
        }
      }
    }
  }, [isRefresh, orders, isChinaTrackerLoaded, loading]); // Added loading

  useEffect(() => {
    if (shouldStackFilters) {
      setAreFiltersExpanded(false);
    } else {
      setAreFiltersExpanded(true);
    }
  }, [shouldStackFilters]);

  useEffect(() => {
    setTableHeaders(getFilteredHeaders(getAllTrackerHeaders()));

    setNestedTableHeaders(
      translateHeaders(
        nestedOrderTrackingHeaders,
        t,
        chinaTrackerTranslationMap,
      ),
    );
  }, [isLargeDevice, isMediumDevice, isSmallDevice, isMobileDevice, language, hiddenColumns, columnOrder]);

  useEffect(() => {
    setBellStatus(unRevisedRejectedOrders?.length > 0);
  }, [unRevisedRejectedOrders]);

  useEffect(() => {
    if (userInfo?.team === "china") {
      if (!isLanguageSet) {
        ConfigurationDispatcher(actionTypesConfigurations.Language, "zh");
        dispatch(setChinaLanguage());
      }
      
      // Hide the bottom tab bar for China team
      navigation.getParent()?.setOptions({
        tabBarStyle: { display: "none" },
      });
    } else {
      // Ensure it is visible for other users
      navigation.getParent()?.setOptions({
        tabBarStyle: undefined,
      });
    }
  }, [isLanguageSet, userInfo?.team, navigation]);

  const handleSearch = (searchedText: string) => {
    setPage(0); // reset the page on search
    if (searchedText) {
      dispatch(searchTrackerOrders(searchedText));
    } else {
      dispatch(
        filterTrackerOrders({
          items: selectedCategories,
          productionLines: selectedProductionLines,
          productionLine: selectedProductionLine,
          language: language,
        }),
      );
    }
  };
  const renderFilterCard = () => (
    <View
      style={[
        styles.sectionCard,
        styles.denseCard,
        shouldUseCompactControls && styles.sectionCardCompact,
      ]}
    >
      {isSmallDevice || isMobileDevice ? (
        <View>
          <View style={styles.mobileFilterRow}>
            <TouchableOpacity
              style={[
                styles.mobileDropdownButton,
                mobileFilterOpen === "production" &&
                styles.mobileDropdownButtonActive,
              ]}
              onPress={() => {
                if (mobileFilterOpen === "production") {
                  setMobileFilterOpen(null);
                } else {
                  setMobileFilterOpen("production");
                  setExpandProductionLine(true);
                }
              }}
            >
              <Text style={styles.mobileDropdownLabel}>
                {t("productionLine")}:{" "}
              </Text>
              <Text style={styles.mobileDropdownValue}>
                {selectedProductionLines.length === 0
                  ? t("all")
                  : selectedProductionLines.length === 1
                    ? `Line ${selectedProductionLines[0]}`
                    : `${selectedProductionLines.length} selected`}
              </Text>
              <Text style={styles.mobileDropdownArrow}>
                {mobileFilterOpen === "production" ? "▲" : "▼"}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.mobileDropdownButton,
                mobileFilterOpen === "status" &&
                styles.mobileDropdownButtonActive,
              ]}
              onPress={() => {
                if (mobileFilterOpen === "status") {
                  setMobileFilterOpen(null);
                } else {
                  setMobileFilterOpen("status");
                  setExpandQuickFilter(true);
                }
              }}
            >
              <Text style={styles.mobileDropdownLabel}>
                {t("quickFilter")}:{" "}
              </Text>
              <Text style={styles.mobileDropdownValue}>
                {selectedCategories.length === 0
                  ? t("all")
                  : selectedCategories.length === 1
                    ? selectedCategories[0]
                    : `${selectedCategories.length} selected`}
              </Text>
              <Text style={styles.mobileDropdownArrow}>
                {mobileFilterOpen === "status" ? "▲" : "▼"}
              </Text>
            </TouchableOpacity>
          </View>
          {mobileFilterOpen === "production" && (
            <View style={[styles.filterColumn, { marginTop: 10 }]}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.filterChipRowCompact}
              >
                <ChinaTrackerProductionLineList
                  itemsList={productionLineFilterList}
                  selectedCategory={selectedCategory}
                  filterCategory={selectedProductionLine}
                  setFilterCategory={(val: string | number) => {
                    setSelectedProductionLine(val);
                  }}
                  tableHeaders={tableHeaders}
                  trackerType="CHINA"
                  expandProductionLine={expandProductionLine}
                  setExpandProductionLine={setExpandProductionLine}
                  multiSelect
                  selectedProductionLines={selectedProductionLines}
                  setSelectedProductionLines={setSelectedProductionLines}
                />
              </ScrollView>
            </View>
          )}
          {mobileFilterOpen === "status" && (
            <View style={[styles.filterColumn, { marginTop: 10 }]}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.filterChipRowCompact}
              >
                <ItemTrackerBadgeHorizontalList
                  productionLine={selectedProductionLine}
                  itemsList={operationsFilterCategoryList}
                  filterCategory={selectedCategory || ""}
                  setFilterCategory={setSelectedCategory!}
                  tableHeaders={tableHeaders}
                  trackerType="CHINA"
                  multiSelect
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                />
              </ScrollView>
            </View>
          )}
        </View>
      ) : (
        <>
          <View style={styles.cardHeaderRow}>
            <View style={styles.headerLabelGroup}>
              <Text
                style={[
                  styles.sectionTitle,
                  styles.filterColumnLabel,
                  shouldUseCompactControls && styles.sectionTitleCompact,
                ]}
              >
                {t("productionLine")}
              </Text>
              <Text
                style={[
                  styles.sectionTitle,
                  styles.filterColumnLabel,
                  shouldUseCompactControls && styles.sectionTitleCompact,
                ]}
              >
                {t("quickFilter")}
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => setAreFiltersExpanded((prev) => !prev)}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons
                name={areFiltersExpanded ? "chevron-up" : "chevron-down"}
                size={20}
                color="#111827"
              />
            </TouchableOpacity>
          </View>
          {areFiltersExpanded && (
            <View
              style={[
                styles.filterCard,
                shouldStackFilters && styles.filterCardStacked,
              ]}
            >
              <View style={styles.filterColumn}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={[
                    styles.filterChipRow,
                    shouldUseCompactControls && styles.filterChipRowCompact,
                  ]}
                >
                  <ChinaTrackerProductionLineList
                    itemsList={productionLineFilterList}
                    selectedCategory={selectedCategory}
                    filterCategory={selectedProductionLine}
                    setFilterCategory={setSelectedProductionLine!}
                    tableHeaders={tableHeaders}
                    trackerType="CHINA"
                    expandProductionLine={expandProductionLine}
                    setExpandProductionLine={setExpandProductionLine}
                    multiSelect
                    selectedProductionLines={selectedProductionLines}
                    setSelectedProductionLines={setSelectedProductionLines}
                  />
                </ScrollView>
              </View>
              <View style={styles.filterColumn}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={[
                    styles.filterChipRow,
                    shouldUseCompactControls && styles.filterChipRowCompact,
                  ]}
                >
                  <ItemTrackerBadgeHorizontalList
                    productionLine={selectedProductionLine}
                    itemsList={operationsFilterCategoryList}
                    filterCategory={selectedCategory || ""}
                    setFilterCategory={setSelectedCategory!}
                    tableHeaders={tableHeaders}
                    trackerType="CHINA"
                    multiSelect
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                  />
                </ScrollView>
              </View>
            </View>
          )}
        </>
      )}
    </View>
  );

  const handleExportPDF = () => {
    dispatch(getMergedTrackerOrders({ refresh: isRefresh }))
      .unwrap()
      .then(() => {
        exportDataToExcelMultiSheet(
          filteredOrders,
          "OrderTrackerReport",
          ["Main Orders", "Factory Details", "Shipments"],
          t as any,
        );
      })
      .catch((err: any) => {
        console.error("Error exporting PDF: ", err);
      });
  };

  const renderUtilityCards = () => {
    const canReviewRejected =
      userRoles.some((role: string) => ["ADMIN", "OWNER"].includes(role)) ||
      userInfo?.team === "purchasing";

    const alertActions: Array<{
      key: string;
      label: string;
      icon: any;
      color: string;
      textColor?: string;
      onPress?: () => void;
    }> = [
        {
          key: "request",
          label: t("requestDateAlerts"),
          icon: "bell-alert",
          color: incomingBellStatus ? "#C62828" : "#B8B8B8",
          textColor: incomingBellStatus ? "#C62828" : "#B8B8B8",
          onPress: incomingBellStatus
            ? () => navigateToOrder(orders, "request_date_alerts")
            : undefined,
        },
        {
          key: "incoming",
          label: t("incomingOrder"),
          icon: "bell",
          color: incomingBellStatus ? "#00a434" : "#B8B8B8",
          onPress: incomingBellStatus
            ? () => navigateToOrder(orders, "incoming_orders")
            : undefined,
        },
        {
          key: "archived",
          label: t("archievedOrders"),
          icon: "archive-outline",
          color: "#6B7280",
          onPress: () => navigateToOrder(orders, "cancelled_orders"),
        },
      ];

    if (canReviewRejected) {
      alertActions.splice(1, 0, {
        key: "checklist",
        label: t("checklist"),
        icon: "check",
        color: incomingBellStatus ? "#00a434" : "#B8B8B8",
        onPress: incomingBellStatus
          ? () => navigateToOrder(orders, "checklist")
          : undefined,
      });

      alertActions.splice(2, 0, {
        key: "rejected",
        label: t("reviewRejectedOrders"),
        icon: "window-close",
        color: bellStatus ? "#00a434" : "#B8B8B8",
        onPress: bellStatus
          ? () => navigateToOrder(orders, "rejected_orders")
          : undefined,
      });
    }

    return (
      <View
        style={[
          styles.utilityContainer,
          shouldStackUtilityCards && styles.utilityContainerStacked,
        ]}
      >
        <View
          style={[
            styles.sectionCard,
            styles.denseCard,
            styles.alertsCard,
            shouldUseCompactControls && styles.sectionCardCompact,
            (isSmallDevice || isMobileDevice) &&
            actionsExpanded && { paddingBottom: 60 },
          ]}
        >
          <TouchableOpacity
            style={styles.cardHeaderRow}
            onPress={() => {
              if (isSmallDevice || isMobileDevice) {
                setActionsExpanded(!actionsExpanded);
              }
            }}
            activeOpacity={isSmallDevice || isMobileDevice ? 0.7 : 1}
          >
            <Text
              style={[
                styles.sectionTitle,
                shouldUseCompactControls && styles.sectionTitleCompact,
                { marginBottom: 0 },
              ]}
            >
              {t("actions") || "Actions"}
            </Text>
            {(isSmallDevice || isMobileDevice) && (
              <MaterialCommunityIcons
                name={actionsExpanded ? "chevron-up" : "chevron-down"}
                size={20}
                color="#6B7280"
              />
            )}
          </TouchableOpacity>
          {(!(isSmallDevice || isMobileDevice) || actionsExpanded) && (
            <View style={styles.alertActionsWrapper}>
              <View style={styles.alertActionsContainer}>
                {alertActions.map((action) => (
                  <TouchableOpacity
                    key={action.key}
                    style={[
                      styles.alertActionItem,
                      !action.onPress && styles.alertActionDisabled,
                    ]}
                    onPress={action.onPress}
                    activeOpacity={action.onPress ? 0.85 : 1}
                  >
                    <MaterialCommunityIcons
                      name={action.icon}
                      size={18}
                      color={action.color}
                    />
                    <Text
                      style={[
                        styles.alertActionText,
                        action.textColor && { color: action.textColor },
                      ]}
                    >
                      {action.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}
        </View>
        <View
          style={[
            styles.sectionCard,
            styles.denseCard,
            styles.searchCard,
            shouldUseCompactControls && styles.sectionCardCompact,
          ]}
        >
          <LabelBannerWithSearchBar
            titleName={`${t("totalItems")}: ${filteredOrders.length}`}
            handleSearch={handleSearch}
            setRefresh={setRefresh}
            dataToExport={filteredOrders}
            fileName="OrderTrackerReport"
            onExport={() => {
              if (filteredOrders && filteredOrders.length > 0) {
                handleExportPDF();
              }
            }}
            extraControls={
              <RNView style={{ flexDirection: "row", gap: 8 }}>
                {!(isSmallDevice || isMobileDevice) && (
                  <RNView>
                    <TouchableOpacity
                      ref={settingsBtnRef}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: columnSettingsOpen ? "#E0E7FF" : "#F3F4F6",
                        justifyContent: "center",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                      }}
                      onPress={() => {
                        if (settingsBtnRef.current) {
                          settingsBtnRef.current.measureInWindow(
                            (x: number, y: number, width: number, height: number) => {
                              setSettingsBtnPos({ x, y, width, height });
                              setColumnSettingsOpen(!columnSettingsOpen);
                            }
                          );
                        } else {
                          setColumnSettingsOpen(!columnSettingsOpen);
                        }
                      }}
                    >
                      <Feather
                        name="settings"
                        size={15}
                        color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                      />
                    </TouchableOpacity>
                    <Modal
                      visible={columnSettingsOpen}
                      transparent={true}
                      animationType="fade"
                      onRequestClose={() => setColumnSettingsOpen(false)}
                    >
                      <Pressable
                        style={{
                          flex: 1,
                          backgroundColor: "rgba(0,0,0,0.15)",
                        }}
                        onPress={() => setColumnSettingsOpen(false)}
                      >
                        <Pressable
                          style={{
                            position: "absolute",
                            top: settingsBtnPos.y + settingsBtnPos.height + 6,
                            left: settingsBtnPos.x - 220 + 40, // offset left since it's in a row with gap
                            backgroundColor: "#fff",
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: "#E2E8F0",
                            paddingVertical: 10,
                            minWidth: 220,
                            shadowColor: "#000",
                            shadowOffset: { width: 0, height: 8 },
                            shadowOpacity: 0.15,
                            shadowRadius: 20,
                            elevation: 10,
                          }}
                          onPress={(e) => e.stopPropagation()}
                        >
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "700",
                              color: "#6B7280",
                              paddingHorizontal: 16,
                              paddingBottom: 8,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                           {t("showHideColumns")}
                          </Text>
                          <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                          {/* Select All */}
                          <TouchableOpacity
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              paddingVertical: 8,
                              paddingHorizontal: 16,
                            }}
                            onPress={() => {
                              const allKeys = Object.keys(getAllTrackerHeaders()).filter((k) => k !== "");
                              if (hiddenColumns.length === 0) {
                                const updated = [...allKeys];
                                setHiddenColumns(updated);
                                AsyncStorage.setItem("chinatracker_hidden_columns", JSON.stringify(updated));
                              } else {
                                setHiddenColumns([]);
                                AsyncStorage.setItem("chinatracker_hidden_columns", JSON.stringify([]));
                              }
                            }}
                          >
                            <View
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                borderWidth: 1.5,
                                borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                                backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                                justifyContent: "center",
                                alignItems: "center",
                                marginRight: 10,
                              }}
                            >
                              {hiddenColumns.length === 0 && (
                                <Feather name="check" size={13} color="#fff" />
                              )}
                            </View>
                            <Text
                              style={{
                                fontSize: 14,
                                color: "#111827",
                                fontWeight: "700",
                              }}
                            >
                              Select All
                            </Text>
                          </TouchableOpacity>
                          <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                          <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                            {getOrderedKeys(getAllTrackerHeaders()).map((key) => {
                              if (key === "") return null;
                              const value = getAllTrackerHeaders()[key];
                              if (!value) return null;
                              if ((value as any).display === "none" || (value as any).display === "hidden") return null;
                              const isVisible = !hiddenColumns.includes(key);
                              const isDragging = dragActiveKey === key;
                              const isDragOver = dragOverKey === key;
                              return (
                                <RNView
                                  key={key}
                                  {...{ dataSet: { columnkey: key } } as any}
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    paddingVertical: 8,
                                    paddingHorizontal: 16,
                                    opacity: isDragging ? 0.35 : 1,
                                    backgroundColor: isDragOver ? "#EFF6FF" : "transparent",
                                    borderTopWidth: isDragOver ? 2 : 0,
                                    borderTopColor: "#56B7FD",
                                  }}
                                >
                                  <Pressable
                                    onPressIn={() => startColumnDrag(key)}
                                    style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                  >
                                    <Feather name="menu" size={14} color="#9CA3AF" />
                                  </Pressable>
                                  <TouchableOpacity
                                    style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                    onPress={() => toggleColumn(key)}
                                  >
                                    <RNView
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 4,
                                        borderWidth: 1.5,
                                        borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                        backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginRight: 10,
                                      }}
                                    >
                                      {isVisible && (
                                        <Feather name="check" size={13} color="#fff" />
                                      )}
                                    </RNView>
                                    <Text
                                      style={{
                                        fontSize: 14,
                                        color: isVisible ? "#111827" : "#9CA3AF",
                                        fontWeight: isVisible ? "600" : "400",
                                      }}
                                    >
                                      {(value as any).name}
                                    </Text>
                                  </TouchableOpacity>
                                </RNView>
                              );
                            })}
                          </ScrollView>
                          {columnOrder.length > 0 && (
                            <>
                              <RNView style={{ height: 1, backgroundColor: "#F1F5F9", marginTop: 4 }} />
                              <TouchableOpacity
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  paddingVertical: 10,
                                  paddingHorizontal: 16,
                                }}
                                onPress={resetColumnOrder}
                              >
                                <Feather name="rotate-ccw" size={13} color="#6B7280" />
                                <Text style={{ fontSize: 13, color: "#6B7280", fontWeight: "600", marginLeft: 6 }}>
                                  Reset Order
                                </Text>
                              </TouchableOpacity>
                            </>
                          )}
                        </Pressable>
                      </Pressable>
                    </Modal>
                  </RNView>
                )}
                <TouchableOpacity
                  style={{
                    width: isSmallDevice || isMobileDevice ? 34 : 40,
                    height: isSmallDevice || isMobileDevice ? 34 : 40,
                    borderRadius: isSmallDevice || isMobileDevice ? 8 : 10,
                    backgroundColor: "#F3F4F6",
                    justifyContent: "center",
                    alignItems: "center",
                    borderWidth: 1,
                    borderColor: "#E2E8F0",
                  }}
                  onPress={() => navigateToOrder(null, "advanced_search")}
                  activeOpacity={0.7}
                >
                  <MaterialCommunityIcons
                    name="magnify-plus-outline"
                    size={isSmallDevice || isMobileDevice ? 18 : 20}
                    color="#6B7280"
                  />
                </TouchableOpacity>
              </RNView>
            }
          />
        </View>
      </View>
    );
  };

  const onHandleSortColumn = (columnKey: string) => {
    if (sortDirection === "desc") {
      dispatch(sortTrackerOrdersByOrderNumber("asc"));
    } else {
      dispatch(sortTrackerOrdersByOrderNumber("desc"));
    }
  };

  const navigatePressAction = (filterField: string, filterValue: string) => {
    if (
      filterValue !== undefined &&
      filterValue !== null &&
      !(typeof filterValue === "string" && filterValue.trim() === "")
    ) {
      // dispatch here
      if (["OrderNumber1", "MSRef", "StateDelivery"].includes(filterField)) {
        dispatch(
          filterSpecificField({
            filterField: filterField,
            filterValue: filterValue,
          }),
        );
      } else if (["OrderDate", "ETA"].includes(filterField)) {
        // show calender
        setModalOpen(true);
        setSelectedItem(filterValue.trim());
        setSelectedEnquiryType("date");
      }
    }
  };

  return (
    <View style={styles.container}>
      {!((isSmallDevice || isMobileDevice) && contentOffSet > 50) && (
        <View style={styles.topContent}>
          {renderFilterCard()}
          {renderUtilityCards()}
        </View>
      )}

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            <ChinaTrackerDataTableWithPagination
              isToExpandAllRows={false}
              isRowsExpandable={true}
              headers={tableHeaders}
              itemsPerPage={20}
              isNestedDataLoaded={loading}
              rows={filteredOrders}
              key="rowKeyValue"
              rowKeyValue="Key"
              columnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandsSeparator
              }
              handleSortColumn={onHandleSortColumn}
              operationActions={navigateToOrder}
              itemTracker={true}
              // Nested Props
              nestedHeader={nestedTableHeaders}
              nestedItemKeyValue="details"
              // Required props
              useModal={false}
              setModalVisible={() => { }}
              handleRowClick={() => { }}
              setSelected={() => { }}
              handleDeleteRow={() => { }}
              navigatePressAction={navigatePressAction}
              t={t}
              page={page}
              setPage={setPage}
              selectable={true}
            />
          </View>
        )}
      </ScrollView>

      {modalOpen && selectedEnquiryType === "flag" && (
        <ChinaTrackerFlagModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "interrupt" && (
        <ChinaTrackerInterruptRunOrder
          onClose={onClose}
          visible={modalOpen}
          runOrder={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "incoming_orders" && (
        <ChinaTrackerIncomingOrders
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "cancelled_orders" && (
        <ChinaTrackerCancelledOrders
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "advanced_search" && (
        <ChinaTrackerAdvancedSearch
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "request_date_alerts" && (
        <ChinaTrackerRequestDateAlertsModal
          onClose={onClose}
          visible={modalOpen}
        />
      )}

      {modalOpen && selectedEnquiryType === "rejected_orders" && (
        <ChinaTrackerRejectedOrders
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "checklist" && (
        <ChecklistModal onClose={onClose} visible={modalOpen} />
      )}

      {modalOpen && selectedEnquiryType === "add" && (
        <TrackerEnquiryModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "history" && (
        <TrackerHistoryModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "shipments" && (
        <ChinaShipmentsListModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "date" && (
        <TrackerDateModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "priority" && (
        <TrackerPriorityModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "itemCodeReport" && (
        <ChinaTrackerItemCodeInfoModal
          onClose={onClose}
          visible={modalOpen}
          itemCode={selectedItem}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F4F6F8",
  },
  pageScroll: {
    flex: 1,
  },
  pageScrollContent: {
    padding: 16,
    paddingBottom: 32,
    gap: 16,
  },
  topContent: {
    gap: 12,
    marginBottom: 10,
  },
  sectionCard: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 12,
    shadowColor: "rgba(15, 23, 42, 0.08)",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 2,
  },
  denseCard: {
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  sectionCardCompact: {
    padding: 8,
    borderRadius: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
    marginBottom: 6,
  },
  sectionTitleCompact: {
    fontSize: 12,
    marginBottom: 4,
  },
  filterChipRow: {
    paddingRight: 8,
    paddingBottom: 2,
  },
  filterChipRowCompact: {
    paddingBottom: 0,
  },
  filterCard: {
    flexDirection: "row",
    gap: 12,
  },
  filterCardStacked: {
    flexDirection: "column",
  },
  cardHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  headerLabelGroup: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  filterColumnLabel: {
    flex: 1,
  },
  filterColumn: {
    flex: 1,
  },
  utilityContainer: {
    flexDirection: "row",
    gap: 16,
  },
  utilityContainerStacked: {
    flexDirection: "column",
  },
  alertsCard: {
    flex: 1,
    paddingBottom: 14,
  },
  alertActionsWrapper: {
    justifyContent: "center",
    paddingTop: 4,
    paddingBottom: 4,
  },
  alertActionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-start",
    alignItems: "center",
  },

  alertActionItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: "#F1F5F9",
  },
  alertActionDisabled: {
    opacity: 0.5,
  },
  alertActionText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#111827",
  },
  searchCard: {
    flex: 1,
    justifyContent: "center",
  },
  tableCard: {
    padding: 0,
    overflow: "hidden",
  },
  spinnerCard: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
  },
  contentContainer: {
    flex: 1,
  },
  spinnerContainer: {
    position: "absolute",
    top: 100,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
  },
  mobileFilterRow: {
    flexDirection: "row",
    paddingVertical: 8,
    gap: 16,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
});
